import { z } from "zod";
import { isValidCron } from "../lib/cron";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro e-mailové kampaně (plán §S.2.9).
 *
 * Kampaň kombinuje šablonu + audience (štítek) + společný payload. Běh
 * kampaně produkuje 1:N sends (fan-out). Scheduler cron je deferred,
 * tahle iterace řeší manuální "Run Now".
 */

const uuidMsg = "Neplatné UUID";

const nazevField = z
  .string()
  .min(1, "Název je povinný")
  .max(100, "Název musí být 100 znaků nebo méně");

const payloadField = z
  .record(z.string().max(100, "Klíč proměnné musí být 100 znaků nebo méně"), z.string().max(5000))
  .default({});

const cronField = z
  .string()
  .max(100, "Cron expression musí být 100 znaků nebo méně")
  .refine((s) => s.length === 0 || isValidCron(s), {
    message: "Neplatný cron expression (očekávaný formát: min hour day month weekday)",
  });

export const createEmailCampaignSchema = z.object({
  nazev: nazevField,
  templateId: z.string().uuid(uuidMsg),
  audienceTagId: z.string().uuid(uuidMsg),
  defaultPayload: payloadField,
  aktivni: z.boolean().default(true),
  cronExpression: cronField.optional(),
});

export const updateEmailCampaignSchema = z.object({
  id: z.string().uuid(uuidMsg),
  nazev: nazevField.optional(),
  templateId: optionalUuid(uuidMsg),
  audienceTagId: optionalUuid(uuidMsg),
  defaultPayload: z
    .record(z.string().max(100, "Klíč proměnné musí být 100 znaků nebo méně"), z.string().max(5000))
    .optional(),
  aktivni: z.boolean().optional(),
  cronExpression: cronField.optional(),
});

/** Whitelist řazení kampaní — router mapuje klíče na sloupce. */
export const EMAIL_CAMPAIGNS_SORT_KEYS = ["nazev", "stav", "aktualizovano"] as const;
export type EmailCampaignsSortKey = (typeof EMAIL_CAMPAIGNS_SORT_KEYS)[number];

export const listEmailCampaignsSchema = paginationSchema
  .merge(sortSchema(EMAIL_CAMPAIGNS_SORT_KEYS))
  .extend({
    search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
    aktivniOnly: z.coerce.boolean().optional(),
  });

export const emailCampaignIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Manuální trigger kampaně — volitelně override společného payloadu. */
export const runEmailCampaignNowSchema = z.object({
  id: z.string().uuid(uuidMsg),
  overridePayload: z
    .record(z.string().max(100, "Klíč proměnné musí být 100 znaků nebo méně"), z.string().max(5000))
    .optional(),
});

/** Admin dashboard — detail kampaňového runu se stavem jednotlivých odeslání. */
export const getCampaignRunDetailSchema = paginationSchema.extend({
  runId: z.string().uuid(uuidMsg),
});

/** Náhled audience před Run Now — admin vidí kdo je v cílové skupině. */
export const previewRecipientsSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Range pro analytics page — denní volume + top kampaně. */
export const getEmailAnalyticsSchema = z.object({
  days: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30),
});

export type CreateEmailCampaignInput = z.infer<typeof createEmailCampaignSchema>;
export type UpdateEmailCampaignInput = z.infer<typeof updateEmailCampaignSchema>;
export type ListEmailCampaignsInput = z.infer<typeof listEmailCampaignsSchema>;
export type EmailCampaignIdInput = z.infer<typeof emailCampaignIdSchema>;
export type RunEmailCampaignNowInput = z.infer<typeof runEmailCampaignNowSchema>;
export type GetCampaignRunDetailInput = z.infer<typeof getCampaignRunDetailSchema>;
export type PreviewRecipientsInput = z.infer<typeof previewRecipientsSchema>;
export type GetEmailAnalyticsInput = z.infer<typeof getEmailAnalyticsSchema>;
